import type {
  WaRiskBand,
  WaRiskPreviewResult,
  WaRiskQuestion,
  WaRiskQuestionOption,
  WaRiskQuestionsPayload
} from 'types/whatsapp';

/** Aceita `{ data: T }` ou corpo direto (como o backend atual do questions). */
export function unwrapWaApiPayload<T>(raw: unknown): T {
  if (raw == null || typeof raw !== 'object') return raw as T;
  const body = raw as Record<string, unknown>;
  if ('data' in body && body.data != null && typeof body.data === 'object') {
    return body.data as T;
  }
  return raw as T;
}

export function normalizeWaRiskQuestion(raw: unknown): WaRiskQuestion {
  const q = (raw ?? {}) as Record<string, unknown>;
  const options = Array.isArray(q.options)
    ? (q.options as Record<string, unknown>[]).map((o) => ({
        value: String(o.value ?? ''),
        label: String(o.label ?? o.value ?? ''),
        weight: typeof o.weight === 'number' ? o.weight : undefined
      }))
    : undefined;

  const id = String(q.questionId ?? q.id ?? '');
  return {
    questionId: id,
    id: String(q.id ?? q.questionId ?? id),
    label: String(q.label ?? q.text ?? id),
    text: q.text != null ? String(q.text) : undefined,
    helpText: q.helpText != null ? String(q.helpText) : null,
    type: (q.type as WaRiskQuestion['type']) ?? (options?.length ? 'single_choice' : 'boolean'),
    options,
    required: q.required !== false
  };
}

export function normalizeWaRiskQuestionsPayload(raw: unknown): WaRiskQuestionsPayload {
  const p = unwrapWaApiPayload<Record<string, unknown>>(raw);
  const questions = Array.isArray(p.questions) ? p.questions.map(normalizeWaRiskQuestion) : [];
  return {
    schemaVersion:
      typeof p.schemaVersion === 'string' || typeof p.schemaVersion === 'number' ? p.schemaVersion : '1',
    consentText: String(p.consentText ?? ''),
    questions
  };
}

export function normalizeWaRiskPreviewResult(raw: unknown): WaRiskPreviewResult {
  const p = unwrapWaApiPayload<Record<string, unknown>>(raw);
  return {
    score: Number(p.score ?? 0),
    band: String(p.band ?? 'low') as WaRiskBand,
    topFactors: Array.isArray(p.topFactors) ? p.topFactors.map(String) : [],
    schemaVersion: String(p.schemaVersion ?? '1')
  };
}

export const WA_RISK_YES_NO_OPTIONS: WaRiskQuestionOption[] = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' }
];

export function waRiskQuestionId(question: WaRiskQuestion): string {
  return question.questionId || question.id || '';
}

export function waRiskQuestionLabel(question: WaRiskQuestion): string {
  return question.label || question.text || question.questionId || '';
}

export function waRiskQuestionOptions(question: WaRiskQuestion): WaRiskQuestionOption[] {
  if (question.options?.length) return question.options;
  if (question.type === 'boolean') return WA_RISK_YES_NO_OPTIONS;
  return [];
}

export function waRiskBandMeta(band: WaRiskBand): {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
  meterColor: string;
} {
  switch (band) {
    case 'low':
      return { label: 'Baixo', color: 'success', meterColor: '#2e7d32' };
    case 'moderate':
      return { label: 'Moderado', color: 'warning', meterColor: '#ed6c02' };
    case 'elevated':
      return { label: 'Elevado', color: 'warning', meterColor: '#d84315' };
    case 'high':
      return { label: 'Alto', color: 'error', meterColor: '#c62828' };
    default:
      return { label: band, color: 'default', meterColor: '#757575' };
  }
}

/** 0–29 low … 120+ high — para posicionar o medidor visual. */
export function waRiskScorePercent(score: number): number {
  return Math.min(100, Math.max(0, (score / 120) * 100));
}

export function scoreToWaRiskBand(score: number): WaRiskBand {
  if (score <= 29) return 'low';
  if (score <= 69) return 'moderate';
  if (score <= 119) return 'elevated';
  return 'high';
}

/** Soma os weights das opções já respondidas (mesma lógica do backend). */
export function computeWaRiskScoreFromAnswers(
  questions: WaRiskQuestion[],
  answers: Record<string, string>
): number {
  let score = 0;
  for (const q of questions) {
    const id = waRiskQuestionId(q);
    const value = answers[id];
    if (!value) continue;
    const opt = waRiskQuestionOptions(q).find((o) => o.value === value);
    if (opt && typeof opt.weight === 'number') score += opt.weight;
  }
  return score;
}

export function computeWaRiskTopFactors(
  questions: WaRiskQuestion[],
  answers: Record<string, string>,
  limit = 3
): string[] {
  const ranked: { id: string; weight: number }[] = [];
  for (const q of questions) {
    const id = waRiskQuestionId(q);
    const value = answers[id];
    if (!value) continue;
    const opt = waRiskQuestionOptions(q).find((o) => o.value === value);
    if (!opt || typeof opt.weight !== 'number') continue;
    ranked.push({ id, weight: opt.weight });
  }
  return ranked
    .sort((a, b) => b.weight - a.weight)
    .slice(0, limit)
    .map((r) => r.id);
}

export function buildLocalWaRiskPreview(
  schema: WaRiskQuestionsPayload,
  answers: Record<string, string>
): WaRiskPreviewResult | null {
  if (!buildWaRiskAnswersList(answers).length) return null;
  const score = computeWaRiskScoreFromAnswers(schema.questions, answers);
  return {
    score,
    band: scoreToWaRiskBand(score),
    topFactors: computeWaRiskTopFactors(schema.questions, answers),
    schemaVersion: String(schema.schemaVersion)
  };
}

export function countWaRiskAnswered(questions: WaRiskQuestion[], answers: Record<string, string>): number {
  return questions.filter((q) => {
    const id = waRiskQuestionId(q);
    const v = answers[id];
    return v !== undefined && v !== '';
  }).length;
}

export function buildWaRiskAnswersList(answers: Record<string, string>): { questionId: string; value: string }[] {
  return Object.entries(answers)
    .filter(([, value]) => value !== '')
    .map(([questionId, value]) => ({ questionId, value }));
}

export function areWaRiskAnswersComplete(questions: WaRiskQuestion[], answers: Record<string, string>): boolean {
  return questions.every((q) => {
    const id = waRiskQuestionId(q);
    const value = answers[id];
    return value !== undefined && value !== '';
  });
}
